import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { TrustBadge } from "@/components/shared";
import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";
import { BlueprintForm, WeightsCard } from "@/components/assessment/BlueprintForm";
import { AssessmentVersionComparison } from "@/components/AssessmentVersionComparison";
import { TeamQuestionReviews } from "@/components/TeamQuestionReviews";
import { countItems } from "@/components/assessment/model";

export function BlueprintSheet({ open, onOpenChange, bp, readOnly, onRegenerate, nextVersion }) {
  const saveOnly = async () => { if (await bp.save()) toast.success("Blueprint saved. The current questions are unchanged."); };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto space-y-5" data-testid="blueprint-sheet">
        <SheetHeader>
          <SheetTitle>Edit blueprint</SheetTitle>
          <SheetDescription>Regenerating writes a new draft, v{nextVersion}. The questions you see now stay saved as they are.</SheetDescription>
        </SheetHeader>
        {!bp.config ? <p className="text-sm text-muted-foreground">{bp.error || "Loading the blueprint…"}</p> : <>
          <BlueprintForm bp={bp} readOnly={readOnly} />
          <WeightsCard bp={bp} readOnly={readOnly} />
          {bp.error && <p role="alert" className="text-sm text-rose-500" data-testid="qa-error">{bp.error}</p>}
          <div className="sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-3 border-t border-border bg-background/95 backdrop-blur flex flex-wrap justify-end gap-2" data-testid="blueprint-sheet-actions">
            <Button variant="outline" disabled={readOnly || !bp.valid} onClick={saveOnly} data-testid="qa-save">Save</Button>
            <Button disabled={readOnly || !bp.valid} onClick={onRegenerate} data-testid="qa-generate" className="gap-1.5"><Sparkles className="h-4 w-4" />Regenerate as v{nextVersion}</Button>
          </div>
        </>}
      </SheetContent>
    </Sheet>
  );
}

function History({ versions, selected, activeVersion, onSelect }) {
  return (
    <div className="space-y-2">
      {versions.map((v) => (
        <button key={v.version} onClick={() => onSelect(v.version)} data-testid={`version-${v.version}`}
          className={cn("w-full rounded-xl border p-4 text-left flex items-center gap-3 transition-colors", v.version === selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40")}>
          <span className="hs-mono text-sm font-semibold w-8">v{v.version}</span>
          <span className="flex-1 min-w-0 text-sm text-muted-foreground truncate">{countItems(v)} questions{v.created_at ? ` · ${new Date(v.created_at).toLocaleDateString()}` : ""}{v.created_by ? ` · ${v.created_by}` : ""}</span>
          {activeVersion === v.version ? <TrustBadge type="high">Live</TrustBadge> : <span className="text-xs text-muted-foreground">Draft</span>}
        </button>
      ))}
    </div>
  );
}

export function VersionsSheet({ open, onOpenChange, tab, setTab, jobId, versions, selected, activeVersion, onSelect, onPublish, publishing, readOnly, reviewId }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="versions-sheet">
        <SheetHeader>
          <SheetTitle>Versions and team reviews</SheetTitle>
          <SheetDescription>Every regenerate or accepted edit saves a new version. Candidates only see the version that goes live when you publish the role.</SheetDescription>
        </SheetHeader>
        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList>
            <TabsTrigger value="history" data-testid="versions-tab-history">History</TabsTrigger>
            <TabsTrigger value="compare" data-testid="versions-tab-compare">Compare</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="versions-tab-reviews">Team reviews</TabsTrigger>
          </TabsList>
          <TabsContent value="history" className="pt-3"><History versions={versions} selected={selected} activeVersion={activeVersion} onSelect={onSelect} /></TabsContent>
          <TabsContent value="compare" className="pt-3"><AssessmentVersionComparison jobId={jobId} versions={versions} selectedVersion={selected} activeVersion={activeVersion} onPublish={onPublish} publishing={publishing} readOnly={readOnly} /></TabsContent>
          <TabsContent value="reviews" className="pt-3"><TeamQuestionReviews jobId={jobId} initialReviewId={reviewId} /></TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
